import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Header from './Header';
import Sidebar from './Sidebar';
import ChangePasswordModal from '../modals/ChangePasswordModal';
import TrialExpiryBanner from '../billing/TrialExpiryBanner';
import ImpersonationBanner from '../systemadmin/ImpersonationBanner';
import { OnboardingWizard } from '../onboarding/OnboardingWizard';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
// import { Company } from '../../types'; // Unused

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';

  const { data: companyData } = useQuery({
    queryKey: ['company', user?.companyId ?? 'current'],
    queryFn: async () => {
      // Prefer explicit companyId when available (works for sysadmin sessions too)
      try {
        if (user?.companyId) {
          const byId = await apiService.getCompanyById(user.companyId);
          return byId.data;
        }
      } catch {}
      const response = await apiService.getCompany();
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false, // Don't retry on failure
    enabled: true, // Re-enabled with better error handling
  });

  // Local fallback for header while query loads
  // Only use name and accentColor from localStorage, NOT logoUrl (to avoid showing wrong company's logo)
  const fallbackCompany = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const name = localStorage.getItem('companyName') || undefined;
    const accentColor = localStorage.getItem('companyAccentColor') || undefined;
    if (!name && !accentColor) return undefined;
    return {
      name: name || 'WorkOrder HQ™',
      accentColor,
    } as any;
  }, []);

  // Persist minimal company fields for quick hydration
  useEffect(() => {
    if (companyData) {
      try {
        if (companyData.name) localStorage.setItem('companyName', companyData.name);
        if (companyData.logoUrl) localStorage.setItem('companyLogoUrl', companyData.logoUrl);
        if (companyData.accentColor) localStorage.setItem('companyAccentColor', companyData.accentColor);
      } catch {}
    }
  }, [companyData]);

  const company = companyData || fallbackCompany;

  useEffect(() => {
    if (user?.forcePasswordChange && !isSystemAdmin) {
      setShowChangePasswordModal(true);
    }
  }, [user?.forcePasswordChange, isSystemAdmin]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePasswordChangeSuccess = async () => {
    setShowChangePasswordModal(false);

    // Fetch fresh user data from the backend
    try {
      const updatedUser = await apiService.getCurrentUser();
      if (updatedUser) {
        // Update localStorage with fresh user data (with forcePasswordChange = false)
        localStorage.setItem('user', JSON.stringify(updatedUser));

        // Reload the page to refresh all components with updated user data
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to fetch updated user data:', error);

      // Fallback: manually update the flag and reload
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          userData.forcePasswordChange = false;
          localStorage.setItem('user', JSON.stringify(userData));
          window.location.reload();
        } catch (err) {
          console.error('Failed to update user data:', err);
        }
      }
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50 dark:bg-gray-900">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <ImpersonationBanner />
        <TrialExpiryBanner />
        <Header 
          company={company} 
          onMobileMenuToggle={() => setSidebarOpen(true)} 
        />

        <main className="flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                © {new Date().getFullYear()} dp Software. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />

      {/* Onboarding Wizard - rendered globally so it works from any page */}
      <OnboardingWizard />
    </div>
  );
};

export default Layout;
